/**
 * The mosaic (Design Review V-7; PRD 10.3; Phase 43): the capacity heatmap drawn as a Marimekko,
 * so that overload is read in **hours** rather than in ratio.
 *
 * Every other reading of this response is a ratio, and a ratio has no size. A QA role of half a
 * person at 175 per cent and a development team of ten at 105 per cent are a dark red cell and a
 * pale amber one; they are eighteen hours and a hundred and twenty. The matrix, the multiples,
 * the horizon and the bullet rows all rank the first above the second, and only one of them is
 * worth a hire. This is the view that says so:
 *
 *   - **column width is capacity** — the role's hours, so the column is as wide as the role is
 *     expensive, and a narrow column is a role that cannot matter much however red it is;
 *   - **column height is allocation over capacity** — the same ratio the matrix cell shows,
 *     against the same threshold bands;
 *   - therefore **area is allocated hours**, and the area poking above the 100 per cent line is
 *     the overload in hours. That is V-7's whole encoding, and it is the reason the two figures
 *     a planner has to hold together — how bad, and how big — are one shape here.
 *
 * It draws either the **window** (V-7's "chosen window", and the default) or a single **week**.
 * In week span the columns are the bullet rows: literally, `bulletModel` is called and its rows
 * are the columns, so the plan's gate — "each column's hours-over label equals the bullet row's
 * hours over" — is true by construction rather than by care, and the two cards under one another
 * on the page cannot state different hours for the same role.
 *
 * Over a window, hours over are counted **per week and added**, never netted: a role twenty
 * hours over in one week and twenty spare in the next is not a role in balance, it is a role
 * with a problem in the first week. The area above the line is the netted figure, because
 * geometry can do nothing else with a window's totals, so the model carries both and the card
 * states both whenever they disagree.
 *
 * Every number is a field of the `GET /forecast/heatmap` cells: this module proportions and
 * sums, and computes no allocation of its own. Shared by the browser client, the CSV export and
 * the tests. Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { bulletModel, resolveWeek } from './bullet.js';
import { ROW_NOUNS, bandOf, round2, thresholdScale } from './heatmap.js';
import { FOCUS_CEILING, SCALE_HEADROOM, SCALE_MODES } from './multiples.js';
import { ticks } from './scales.js';

/**
 * What the columns are summed over: the whole window (V-7's own framing) or one week. Called a
 * span rather than a scope because a scope in this codebase is what a reader is allowed to see.
 */
export const MOSAIC_SPANS = Object.freeze(['window', 'week']);

/**
 * The orders the card offers. `capacity` is the default and puts the widest column first, which
 * is the order a Marimekko is normally read in and the one the plan's gate checks.
 */
export const MOSAIC_SORTS = Object.freeze(['capacity', 'over', 'allocation', 'label']);

/** The two shared value scales, the small multiples' own: fit to the worst column, or clip. */
export const MOSAIC_SCALES = SCALE_MODES;

/**
 * The ceiling the `focus` scale tops out at, and this view's **default**, for the beeswarm's
 * reason: the 100 per cent line is what every column is read against, and a single narrow role
 * at 842 per cent would push it into the bottom twelfth of the plot, where the bands cannot be
 * told apart. A clipped column is marked, counted and stated in hours.
 */
export const MOSAIC_CEILING = FOCUS_CEILING;

/** The gap between columns, in pixels, taken out of the width before the shares are cut. */
export const COLUMN_GAP = 3;

/**
 * A column this wide or narrower is a hairline: drawn, never widened — width is the encoding —
 * but counted, so the card can say that some roles are too small to see rather than leaving a
 * reader to conclude they are missing.
 */
export const HAIRLINE = 4;

/**
 * Roughly how wide a character of the axis label is, at the 11px sans the frame's ticks use.
 * An average, not a measurement: the labels are trimmed to what fits with a character to spare,
 * and the exact figure is in the tooltip and the table either way.
 */
export const CHAR_WIDTH = 6.6;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const hours = (v) => round2(num(v) ?? 0);
const sum = (list, f) => round2(list.reduce((s, c) => s + (num(f(c)) ?? 0), 0));
const most = (list, f) => list.reduce((m, c) => Math.max(m, num(f(c)) ?? 0), 0);
/** Allocation over capacity at the four decimals the API's own cells carry, so the two agree. */
const ratio = (allocated, capacity) =>
  capacity > 0 ? Math.round((allocated / capacity) * 10000) / 10000 : null;

/**
 * One column's figures from a list of cells — one cell for a week's span, the window's
 * cells for the window's, so the two cannot drift apart in their arithmetic.
 *
 * `over_hours` is the area above the line: allocated less capacity over the whole span, which is
 * what the geometry can draw. `weeks_over_hours` is the same subtraction done week by week and
 * added, which is what a planner has to staff for. They are equal in a week's span and the second
 * is the larger of the two over a window, always.
 */
function columnFrom(source, cells, thresholds) {
  const allocated = sum(cells, (c) => c.allocated_hours);
  const capacity = sum(cells, (c) => c.capacity_hours);
  const high = sum(cells, (c) => c.high_confidence_hours);
  const utilization = ratio(allocated, capacity);
  const weekly = cells.map((c) => ({
    week_start: c.week_start,
    over:
      hours(c.capacity_hours) > 0
        ? round2(Math.max(0, hours(c.allocated_hours) - hours(c.capacity_hours)))
        : 0,
    utilization: num(c.utilization),
  }));
  const peak = weekly.reduce(
    (best, w) =>
      w.utilization !== null && (best === null || w.utilization > best.utilization) ? w : best,
    null,
  );
  return {
    key: source.key,
    label: source.label,
    entity: source.entity ?? null,
    weeks: cells.length,
    week_start: cells[0]?.week_start ?? null,
    week_end: cells.at(-1)?.week_end ?? null,
    allocated_hours: allocated,
    high_confidence_hours: high,
    // What is not high confidence: inferred, analog-derived and pipeline work.
    other_hours: round2(Math.max(0, allocated - high)),
    capacity_hours: capacity,
    utilization,
    committed_utilization: ratio(high, capacity),
    band: capacity > 0 ? bandOf(utilization, thresholds) : null,
    has_capacity: capacity > 0,
    // Hours over are hours over *somebody's* capacity. A row nobody is staffed to is not
    // overloaded, it is unstaffed, and those hours are counted on their own so the two never mix.
    over_hours: capacity > 0 ? round2(Math.max(0, allocated - capacity)) : 0,
    weeks_over_hours: capacity > 0 ? round2(weekly.reduce((s, w) => s + w.over, 0)) : 0,
    weeks_over: weekly.filter((w) => w.over > 0).length,
    headroom_hours: capacity > 0 ? round2(Math.max(0, capacity - allocated)) : 0,
    unstaffed_hours: capacity > 0 ? 0 : allocated,
    peak_utilization: peak?.utilization ?? null,
    peak_week: peak?.week_start ?? null,
    explicit_hours: sum(cells, (c) => c.explicit_hours),
    inferred_hours: sum(cells, (c) => c.inferred_hours),
    recurring_hours: sum(cells, (c) => c.recurring_hours),
    nonproject_hours: sum(cells, (c) => c.nonproject_hours),
    pipeline_hours: sum(cells, (c) => c.pipeline_hours),
    // Counts do not add over weeks — the same person is the same person in week two — so these
    // are the most the row carried in any week of the span.
    people: most(cells, (c) => c.people),
    projects: most(cells, (c) => c.projects),
    opportunities: most(cells, (c) => c.opportunities),
    cells,
  };
}

/** A bullet row read as a column, so a week's span is the bullet rows' own figures, not a copy. */
function columnFromBullet(row, thresholds) {
  const column = columnFrom(row, [row.cell], thresholds);
  return {
    ...column,
    // The bullet row is the authority on everything it states; the mosaic adds only the window
    // fields, which for one week are the same subtraction done once.
    allocated_hours: row.allocated_hours,
    high_confidence_hours: row.high_confidence_hours,
    other_hours: row.other_hours,
    capacity_hours: row.capacity_hours,
    utilization: row.utilization,
    band: row.band,
    has_capacity: row.has_capacity,
    over_hours: row.over_hours,
    weeks_over_hours: row.over_hours,
    weeks_over: row.over_hours > 0 ? 1 : 0,
    headroom_hours: row.headroom_hours,
    unstaffed_hours: row.unstaffed_hours,
  };
}

/**
 * A person's initials: the first letter of their first name and of their last, which is what
 * goes under a column because a name does not fit under one and never did.
 *
 * A single-word name gives a single letter. Nothing here is an identifier — the full name is on
 * the column itself, in the tooltip, in the table and in the export — so this can be as short as
 * the space is.
 *
 * @param {string} name
 */
export function initialsOf(name) {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  const first = [...words[0]][0] ?? '';
  const last = words.length > 1 ? ([...words.at(-1)][0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * The label under each column: **initials at the individual grain**, the row's own label at every
 * other. A service or a department is not a person and its initials would be a riddle — Design
 * and Development are both D — so only people are shortened.
 *
 * Two people with the same initials would put the same two letters under two different columns,
 * so a collision is resolved by growing the surname a letter at a time (`AB` becomes `ABe` and
 * `ABr`) until the set is unique, and by an index if two names are identical. The input is every
 * drawn column, so the labels are a function of the response and not of the order the reader
 * happens to have chosen.
 *
 * @param {object[]} columns
 * @param {string} dimension
 */
export function shortLabels(columns, dimension) {
  const list = columns ?? [];
  if (dimension !== 'individual') return list.map((c) => c.label ?? c.key);
  const names = list.map((c) => String(c.entity?.display_name ?? c.label ?? c.key));
  const labels = names.map(initialsOf);
  const surname = (name) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    return words.length > 1 ? words.at(-1) : (words[0] ?? '');
  };
  for (let extra = 1; extra <= 4; extra += 1) {
    const counts = new Map();
    for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1);
    if ([...counts.values()].every((n) => n === 1)) break;
    labels.forEach((label, i) => {
      if (counts.get(label) === 1) return;
      const rest = surname(names[i]).slice(1, 1 + extra);
      labels[i] = `${initialsOf(names[i])}${rest}`;
    });
  }
  const seen = new Map();
  return labels.map((label) => {
    const n = (seen.get(label) ?? 0) + 1;
    seen.set(label, n);
    return n === 1 ? label : `${label}${n}`;
  });
}

/**
 * The label a column of this width has room for: the whole thing, a trimmed thing, or nothing at
 * all. Trimmed rather than dropped, because three letters of a service name still tell a reader
 * which column they are looking at and an overlapping axis tells them nothing.
 *
 * @param {string} text
 * @param {number} width
 */
export function fitLabel(text, width) {
  const label = String(text ?? '');
  if (!label) return null;
  const room = Math.floor((width - 2) / CHAR_WIDTH);
  if (room >= label.length) return label;
  if (room < 3) return null;
  return `${label.slice(0, room - 1)}…`;
}

/**
 * Order the columns. `capacity` descends by width, which is how a Marimekko is read and what
 * puts the role worth the most money first; `over` and `allocation` descend by the two readings
 * of overload; `label` is the API's own order. Columns with no capacity have no width and no
 * ratio and sort last in every order but `label`.
 *
 * @param {object[]} columns
 * @param {'capacity' | 'over' | 'allocation' | 'label'} by
 */
export function sortMosaicColumns(columns, by = 'capacity') {
  const list = [...(columns ?? [])];
  if (by === 'label') return list;
  const measure = (c) =>
    by === 'over' ? c.weeks_over_hours : by === 'allocation' ? c.utilization : c.capacity_hours;
  return list
    .map((c, i) => ({ c, i, m: c.has_capacity ? measure(c) : null }))
    .sort((a, b) => {
      if (a.m === null && b.m === null) return a.i - b.i;
      if (a.m === null) return 1;
      if (b.m === null) return -1;
      return b.m - a.m || a.i - b.i;
    })
    .map((x) => x.c);
}

/**
 * The value scale every column is measured on: allocation over capacity, never hours.
 *
 * Hours are the *area*, which is what the widths already carry; making the height hours too
 * would draw a role's total twice and would put the 100 per cent line at a different place in
 * every column, which is the one line this chart cannot do without.
 *
 * @param {object[]} columns
 * @param {object} thresholds
 * @param {'fit' | 'focus'} mode
 */
export function mosaicScale(columns, thresholds, mode = 'focus') {
  const t = thresholds ?? { underload_below: 0.85, caution_above: 1, overloaded_above: 1.15 };
  const ceiling = Math.max(MOSAIC_CEILING, round2(t.overloaded_above * 1.1));
  if (mode === 'focus') {
    const axis = ticks([0, ceiling], { count: 4 });
    return { mode, max: axis.domain[1], step: axis.step, values: axis.values };
  }
  const worst = (columns ?? []).reduce((m, c) => Math.max(m, c.utilization ?? 0), 0);
  const axis = ticks([0, Math.max(ceiling, worst * SCALE_HEADROOM)], { count: 4 });
  return { mode, max: axis.domain[1], step: axis.step, values: axis.values };
}

/**
 * Everything the mosaic draws: the columns in their order, the ones with no capacity to be a
 * column of, the company's own total and the scale the heights are measured on.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {{ week?: string | null, span?: 'window' | 'week', sort?: string, scale?: 'fit' | 'focus' }} [opts]
 */
export function mosaicModel(
  data,
  { week = null, span = 'window', sort = 'capacity', scale = 'focus' } = {},
) {
  const totals = data?.totals ?? [];
  if (!totals.length) return null;
  const thresholds = data.thresholds ?? {
    underload_below: 0.85,
    caution_above: 1,
    overloaded_above: 1.15,
  };
  const weekStart = resolveWeek(data, week);
  const inWeekSpan = span === 'week';

  let all;
  let total;
  let unassigned = null;
  if (inWeekSpan) {
    // The bullet rows, read as columns: same rows, same hours, same week.
    const bullet = bulletModel(data, { week: weekStart, sort: 'label' });
    all = (bullet?.rows ?? []).map((row) => columnFromBullet(row, thresholds));
    total = bullet ? columnFromBullet(bullet.total, thresholds) : null;
    unassigned = bullet?.unassigned ? columnFromBullet(bullet.unassigned, thresholds) : null;
  } else {
    all = (data.rows ?? []).map((row) => columnFrom(row, row.cells ?? [], thresholds));
    total = columnFrom({ key: 'company', label: 'Company', entity: null }, totals, thresholds);
    const roleOnly = sum(totals, (t) => t.unassigned_hours);
    if (data.dimension === 'individual' && roleOnly > 0)
      unassigned = columnFrom(
        { key: 'unassigned', label: 'Role-only (nobody named)', entity: null },
        totals.map((t) => ({
          week_start: t.week_start,
          week_end: t.week_end,
          allocated_hours: t.unassigned_hours,
          capacity_hours: 0,
          high_confidence_hours: 0,
          utilization: null,
        })),
        thresholds,
      );
  }

  // A column's width is its capacity, so a row with none has no column to draw. Its hours are
  // real and are stated beside the chart rather than given a width the encoding cannot justify.
  const drawn = sortMosaicColumns(
    all.filter((c) => c.has_capacity),
    sort,
  );
  const unstaffed = all.filter((c) => !c.has_capacity && c.allocated_hours > 0);
  const capacity = sum(drawn, (c) => c.capacity_hours);
  const short = shortLabels(drawn, data.dimension ?? 'company');
  const weeks = totals.map((t) => t.week_start);
  return {
    span,
    week: inWeekSpan
      ? {
          week_start: weekStart,
          week_end: totals.find((t) => t.week_start === weekStart)?.week_end ?? null,
          index: weeks.indexOf(weekStart),
          count: weeks.length,
        }
      : null,
    weeks,
    from: data.from ?? weeks[0] ?? null,
    to: data.to ?? totals.at(-1)?.week_end ?? null,
    columns: drawn.map((c, i) => ({
      ...c,
      // The share of the chart's width this column takes: capacity over the capacity drawn,
      // at the four decimals the cells' own ratios carry, because a role at 3.6 per cent of the
      // money and one at 4.4 are different arguments and both round to 4.
      share: capacity > 0 ? ratio(c.capacity_hours, capacity) : 0,
      // What goes under the column: initials for a person, the label for everything else.
      short_label: short[i],
    })),
    unstaffed,
    unassigned,
    total,
    capacity_hours: capacity,
    allocated_hours: sum(drawn, (c) => c.allocated_hours),
    scale: mosaicScale(drawn, thresholds, scale),
    thresholds,
    dimension: data.dimension ?? 'company',
    sort,
    withheld: data.withheld_count ?? 0,
  };
}

/**
 * Where the columns sit in the frame: widths cut from the plot in proportion to capacity, and
 * heights measured on the value scale.
 *
 * The gaps are taken out of the width **before** the shares are cut, so a column's width is its
 * share of the drawable space and not of the space minus its own gap — otherwise a chart of
 * twenty columns would quietly understate every one of them. A column narrower than a hairline
 * is flagged and never widened: widening it would be a lie about the capacity behind it, and
 * the whole argument of this view is that width is money.
 *
 * @param {ReturnType<typeof mosaicModel>} model
 * @param {{ plot: { left: number, top: number, width: number, height: number }, y: Function, gap?: number }} frame
 */
export function mosaicGeometry(model, { plot, y, gap = COLUMN_GAP }) {
  const columns = model?.columns ?? [];
  const max = model.scale.max;
  const pool = Math.max(0, plot.width - gap * Math.max(0, columns.length - 1));
  const capacity = model.capacity_hours;
  const baseline = y(0);
  const capacityY = y(Math.min(1, max));
  // Each column's edges are cut from the *exact* capacity running total rather than from its
  // rounded share, so the widths tile the plot with no drift and the last column ends where the
  // axis does. The rounded `share` is a figure for a label, never a measurement.
  let used = 0;
  return columns.map((column, index) => {
    const left = plot.left + (capacity > 0 ? (pool * used) / capacity : 0) + gap * index;
    used += column.capacity_hours;
    const right = plot.left + (capacity > 0 ? (pool * used) / capacity : 0) + gap * index;
    const x = round2(left);
    const width = round2(right - left);
    const clipped = column.utilization !== null && column.utilization > max;
    const top = y(Math.min(column.utilization ?? 0, max));
    const committed =
      column.committed_utilization === null ? null : y(Math.min(column.committed_utilization, max));
    const placed = {
      ...column,
      x,
      width,
      centre: round2(x + width / 2),
      y: round2(top),
      height: round2(Math.max(0, baseline - top)),
      capacity_y: round2(capacityY),
      committed_y: committed === null ? null : round2(committed),
      // The part of the column above the 100 per cent line: the overload, as an area in hours.
      over:
        column.over_hours > 0
          ? { y: round2(top), height: round2(Math.max(0, capacityY - top)) }
          : null,
      // The part of the capacity box the work does not fill: the bench, as an area in hours.
      headroom:
        column.headroom_hours > 0
          ? { y: round2(capacityY), height: round2(Math.max(0, top - capacityY)) }
          : null,
      clipped,
      hairline: width <= HAIRLINE,
      // What the axis can actually print under this column. The full name is on the column, in
      // the tooltip and in the table, so a column that has room for nothing loses nothing.
      axis_label: fitLabel(column.short_label ?? column.label, width),
    };
    return placed;
  });
}

/** The threshold bands as the plot's background, in the frame's coordinates. */
export function mosaicBackground(scale, thresholds, y) {
  const states = thresholdScale(thresholds);
  const bands = [];
  let from = 0;
  for (const state of states) {
    const to = state.max === null ? scale.max : Math.min(scale.max, state.max);
    if (to > from)
      bands.push({
        key: state.key,
        label: state.label,
        icon: state.icon,
        range: state.range,
        tint: state.tint,
        token: state.var,
        from,
        to,
        y: round2(y(to)),
        height: round2(y(from) - y(to)),
      });
    from = to;
    if (from >= scale.max) break;
  }
  return bands;
}

/** The figures the card's header and its notes state. */
export function mosaicSummary(model) {
  const columns = model?.columns ?? [];
  let widest = null;
  let worst = null;
  for (const column of columns) {
    if (widest === null || column.capacity_hours > widest.capacity_hours) widest = column;
    if (worst === null || column.weeks_over_hours > worst.weeks_over_hours) worst = column;
  }
  return {
    columns: columns.length,
    capacity_hours: model?.capacity_hours ?? 0,
    allocated_hours: model?.allocated_hours ?? 0,
    // The area above the line, added up: the net figure the geometry can draw.
    over_hours: sum(columns, (c) => c.over_hours),
    // The same subtraction week by week: the figure a planner has to staff for. The two are
    // equal over a week and the second is the larger over a window.
    weeks_over_hours: sum(columns, (c) => c.weeks_over_hours),
    headroom_hours: sum(columns, (c) => c.headroom_hours),
    columns_over: columns.filter((c) => c.weeks_over_hours > 0).length,
    clipped: columns.filter(
      (c) => c.utilization !== null && c.utilization > (model?.scale.max ?? 0),
    ).length,
    unstaffed_columns: model?.unstaffed?.length ?? 0,
    unstaffed_hours: sum(model?.unstaffed ?? [], (c) => c.unstaffed_hours),
    widest,
    worst,
  };
}

/** The sentence a column says, for the tooltip's footer and the screen-reader text. */
export function columnText(column, thresholds) {
  if (!column.has_capacity) return 'nobody staffed to it — hours, but no capacity and no ratio';
  const ratio = `${Math.round(column.utilization * 100)}%`;
  if (column.weeks_over_hours > 0)
    return `${ratio} — ${round2(column.weeks_over_hours)} h over capacity`;
  return column.utilization < thresholds.underload_below
    ? `${ratio} — under the band, ${round2(column.headroom_hours)} h free`
    : `${ratio} — inside the healthy band`;
}

/**
 * The table view's rows: one per column, then the rows with no capacity to draw, then the
 * company. The same numbers the areas are cut from, so the gate's comparison with the bullet
 * rows can be done from two exports rather than by eye.
 */
export function mosaicExportRows(model) {
  const all = [
    ...(model?.columns ?? []),
    ...(model?.unstaffed ?? []),
    ...(model?.unassigned ? [model.unassigned] : []),
    ...(model?.total ? [model.total] : []),
  ];
  return all.map((c) => ({
    row: c.label,
    key: c.key,
    span: model.span === 'week' ? c.week_start : `${model.from} to ${model.to}`,
    weeks: c.weeks,
    capacity_hours: c.capacity_hours,
    capacity_share: c.share ?? null,
    allocated_hours: c.allocated_hours,
    high_confidence_hours: c.high_confidence_hours,
    other_hours: c.other_hours,
    over_hours: c.over_hours,
    weeks_over_hours: c.weeks_over_hours,
    weeks_over: c.weeks_over,
    headroom_hours: c.headroom_hours,
    unstaffed_hours: c.unstaffed_hours,
    utilization: c.utilization,
    band: c.band,
    peak_utilization: c.peak_utilization,
    peak_week: c.peak_week,
    explicit_hours: c.explicit_hours,
    inferred_hours: c.inferred_hours,
    recurring_hours: c.recurring_hours,
    nonproject_hours: c.nonproject_hours,
    pipeline_hours: c.pipeline_hours,
    people: c.people,
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** @param {{ dimension?: string }} opts */
export function mosaicColumns({ dimension = 'company' } = {}) {
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    col.text('span', 'Span'),
    col.hours('capacity_hours', 'Capacity'),
    col.percent('capacity_share', 'Width'),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.hours('other_hours', 'Rest'),
    col.hours('over_hours', 'Over (net)'),
    col.hours('weeks_over_hours', 'Over (by week)'),
    col.count('weeks_over', 'Weeks over'),
    col.hours('headroom_hours', 'Headroom'),
    col.hours('unstaffed_hours', 'Unstaffed'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.percent('peak_utilization', 'Peak week'),
    col.week('peak_week', 'Peak', { type: 'text' }),
    col.hours('explicit_hours', 'Explicit'),
    col.hours('inferred_hours', 'Inferred'),
    col.hours('recurring_hours', 'Recurring'),
    col.hours('nonproject_hours', 'Non-project'),
    col.hours('pipeline_hours', 'Pipeline'),
    col.count('people', 'People'),
  ];
}
